import { InterfaceMassSpringDeformable, } from "gorilla-physics";
import * as THREE from "three";
import { Graphics } from "./Graphics";
import { Line2 } from "three/examples/jsm/lines/Line2";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";
// import { keysPressed } from "./demo";
import { Matrix4 } from "three";
import { FloatArray } from "./type";
export class Simulator {
    constructor(simulator, showGrid = true) {
        this.showHalfspaces = true;
        this.fps = 60;
        this.lastStepTime = 0;
        this.lastWallTime = 0;
        this.simTimeAccumulator = 0;
        this.wallTimeAccumulator = 0;
        this.realtimeRatio = 0;
        this.rtrSmoothingWindow = 0.5; // seconds of wall time to average over
        // Wall time owed to the physics but not yet stepped, in seconds. The substep
        // is fixed while frames arrive at whatever rate the display runs at, so the
        // two only stay in step if the leftover is carried between frames.
        this.stepAccumulator = 0;
        // How much backlog one frame may carry into the next, in frames of 1/fps.
        // Without a cap, a frame that arrives late -- a backgrounded tab, a long
        // compile -- owes more sim time than it can step before the next one is due,
        // and every frame after it owes more still. Past this the backlog is dropped
        // and realtimeRatio reports the shortfall.
        this.maxCatchUpSteps = 1;
        // How long one frame may spend stepping the physics, in ms. Found as the run
        // goes rather than set: it creeps up while frames arrive on time and backs
        // off when one is late (see `run`), so it settles at as much physics as fits
        // in a frame beside the drawing, whatever the scene and the display.
        this.physicsBudgetMs = 8;
        // The display's refresh interval, in ms: the shortest gap between frames seen
        // lately. The shortest rather than the mean, because the mean counts the
        // frames the physics itself made late, and a budget sized off those would
        // only make the next frames later.
        this.refreshMs = 1000 / 60;
        // How long `graphics.render` takes, in ms, smoothed over a few frames.
        this.renderMs = 0;
        // Whether the physics is stopped. The scene still renders and still orbits --
        // this is not a teardown -- but no sim time passes and nothing steps.
        //
        // It exists for work that has to share the main thread with the run loop. A
        // scene with a robot in it spends most of each frame inside `hybrid.step`,
        // and anything reading files or parsing a URDF between `await`s gets whatever
        // is left over, which makes a job that took a second on an empty scene take
        // ten on a busy one.
        this.paused = false;
        this.simulator = simulator;
        this.massSpringDeformable = null;
        this.femDeformable = null;
        this.fluid2D = null;
        this.fluid2DMesh = null;
        this.cloth = null;
        this.mpmDeformable = null;
        this.mpm2DMesh = null;
        this.massSpring = null;
        this.graphics = new Graphics(showGrid);
        this.meshes = new Map();
        this.meshSet = new Map();
        this.edgesMeshes = new Map();
        this.lines = new Map();
    }
    addFluid2D(fluid2D) {
        this.fluid2D = fluid2D;
        let radius = 5e-3;
        const geometry = new THREE.SphereGeometry(radius, 8, 8);
        const material = new THREE.MeshPhysicalMaterial({
            color: 0x3366ff,
            roughness: 0,
            transmission: 1.0,
            ior: 1.33,
            transparent: true,
            opacity: 0.7,
        });
        let particles = fluid2D.particles();
        let count = particles.length / 2;
        const fluid2DMesh = new THREE.InstancedMesh(geometry, material, count);
        fluid2DMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
        fluid2DMesh.frustumCulled = false;
        this.fluid2DMesh = fluid2DMesh;
        this.graphics.scene.add(fluid2DMesh);
        const dummy = new THREE.Object3D();
        for (let i = 0; i < particles.length; i += 2) {
            let x = particles[i];
            let y = 0.0;
            let z = particles[i + 1];
            dummy.position.set(x, y, z);
            dummy.updateMatrix();
            fluid2DMesh.setMatrixAt(i / 2, dummy.matrix);
        }
        fluid2DMesh.instanceMatrix.needsUpdate = true;
    }
    updateFluid2D() {
        let particles = this.fluid2D.particles();
        const dummy = new THREE.Object3D();
        for (let i = 0; i < particles.length; i += 2) {
            let x = particles[i];
            let y = 0.0;
            let z = particles[i + 1];
            dummy.position.set(x, y, z);
            dummy.updateMatrix();
            this.fluid2DMesh.setMatrixAt(i / 2, dummy.matrix);
        }
        this.fluid2DMesh.instanceMatrix.needsUpdate = true;
    }
    addDeformable(deformable) {
        if (deformable instanceof InterfaceMassSpringDeformable) {
            this.massSpringDeformable = deformable;
        }
        else {
            this.femDeformable = deformable;
        }
        const geometry = new THREE.BufferGeometry();
        let vertices = deformable.vertices();
        geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
        let facets = deformable.facets();
        geometry.setIndex(new THREE.BufferAttribute(facets, 1));
        geometry.computeVertexNormals();
        const material = new THREE.MeshPhongMaterial({
            color: 0x2194ce,
            side: THREE.DoubleSide,
            flatShading: true,
        });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.frustumCulled = false; // prevent mesh disappearing
        this.meshes.set("deformable", mesh);
        this.graphics.scene.add(mesh);
    }
    updateDeformable() {
        let bunny = this.meshes.get("deformable");
        let vertices = this.femDeformable.vertices();
        bunny.geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
    }
    addMeshSet(body_index, name, color = 0xffffff) {
        let base_vertices_array = this.simulator.visual_base_vertices(body_index);
        let facets_array = this.simulator.facets(body_index);
        let mesh_set = [];
        for (let i = 0; i < base_vertices_array.length; i++) {
            let base_vertices = base_vertices_array[i];
            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute("position", new THREE.BufferAttribute(base_vertices, 3));
            let facets = facets_array[i];
            geometry.setIndex(new THREE.BufferAttribute(facets, 1));
            geometry.computeVertexNormals();
            const material = new THREE.MeshPhongMaterial({
                color: color,
                side: THREE.DoubleSide,
                flatShading: true,
            });
            const mesh = new THREE.Mesh(geometry, material);
            mesh.frustumCulled = false; // prevent mesh disappearing
            mesh_set.push(mesh);
            this.graphics.scene.add(mesh);
        }
        this.meshSet.set(name, mesh_set);
    }
    addMeshSetWithMaterial(body_index, name, material) {
        let base_vertices_array = this.simulator.visual_base_vertices(body_index);
        let facets_array = this.simulator.facets(body_index);
        let mesh_set = [];
        for (let i = 0; i < base_vertices_array.length; i++) {
            let base_vertices = base_vertices_array[i];
            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute("position", new THREE.BufferAttribute(base_vertices, 3));
            let facets = facets_array[i];
            geometry.setIndex(new THREE.BufferAttribute(facets, 1));
            geometry.computeVertexNormals();
            const mesh = new THREE.Mesh(geometry, material);
            mesh.frustumCulled = false; // prevent mesh disappearing
            mesh_set.push(mesh);
            this.graphics.scene.add(mesh);
        }
        this.meshSet.set(name, mesh_set);
    }
    updateMeshSet(body_index, name) {
        let mesh_set = this.meshSet.get(name);
        let iso = this.simulator.isometry(body_index);
        for (let mesh of mesh_set) {
            mesh.position.set(iso[3], iso[4], iso[5]);
            mesh.rotation.set(iso[0], iso[1], iso[2], "ZYX"); // Note: rotation is performed along local coordinate axes, so we should do ZYX order to make it match XYZ order on global axes rotation.
        }
    }
    addCuboid(name, color, w, d, h, offset = new THREE.Matrix4()) {
        const geometry = new THREE.BoxGeometry(w, d, h);
        geometry.applyMatrix4(offset);
        const material = new THREE.MeshPhongMaterial({
            color: color,
            side: THREE.DoubleSide,
            flatShading: true,
        });
        const box = new THREE.Mesh(geometry, material);
        this.meshes.set(name, box);
        this.graphics.scene.add(box);
        const edges = new THREE.EdgesGeometry(geometry);
        const edgesMaterial = new THREE.LineBasicMaterial({ color: 0x000000 });
        const edgesMesh = new THREE.LineSegments(edges, edgesMaterial);
        this.edgesMeshes.set(name, edgesMesh);
        this.graphics.scene.add(edgesMesh);
    }
    addSphere(name, color, radius, offset = new THREE.Matrix4()) {
        const geometry = new THREE.SphereGeometry(radius, 32, 32);
        geometry.applyMatrix4(offset);
        const material = new THREE.MeshPhongMaterial({
            color: color,
            side: THREE.DoubleSide,
            flatShading: true,
            opacity: 0.5,
            transparent: true,
        });
        const sphere = new THREE.Mesh(geometry, material);
        this.meshes.set(name, sphere);
        this.graphics.scene.add(sphere);
    }
    addLine(name, color, linewidth) {
        const geometry = new LineGeometry();
        const material = new LineMaterial({
            color: color,
            linewidth: linewidth,
            resolution: new THREE.Vector2(window.innerWidth, window.innerHeight), // Required for LineMaterial
        });
        const line = new Line2(geometry, material);
        this.lines.set(name, line);
        this.graphics.scene.add(line);
    }
    setPose(name, poses) {
        let quaternion = new THREE.Quaternion();
        quaternion.set(poses[0], poses[1], poses[2], poses[3]);
        let pos = [poses[4], poses[5], poses[6]];
        let body = this.meshes.get(name);
        if (body) {
            body.rotation.setFromQuaternion(quaternion, "ZYX");
            body.position.set(pos[0], pos[1], pos[2]);
        }
        let edges = this.edgesMeshes.get(name);
        if (edges) {
            edges.rotation.setFromQuaternion(quaternion, "ZYX");
            edges.position.set(pos[0], pos[1], pos[2]);
        }
    }
    setLineEndpoints(name, pos1, pos2) {
        this.lines
            .get(name)
            .geometry.setPositions([
            pos1[0],
            pos1[1],
            pos1[2],
            pos2[0],
            pos2[1],
            pos2[2],
        ]);
    }
    addCart(length) {
        this.length = length;
        const cartGeometry = new THREE.BoxGeometry(length, 1, 1);
        const cartMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
        this.cart = new THREE.Mesh(cartGeometry, cartMaterial);
        this.graphics.scene.add(this.cart);
    }
    addCartPole(length) {
        const cartGeometry = new THREE.BoxGeometry(3, 0.1, 0.1);
        const cartMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
        const cart = new THREE.Mesh(cartGeometry, cartMaterial);
        this.meshes.set("cart", cart);
        this.graphics.scene.add(cart);
        const poleGeometry = new THREE.CylinderGeometry(0.1, 0.1, length, 32);
        const poleMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
        const pole = new THREE.Mesh(poleGeometry, poleMaterial);
        pole.position.y = -length / 2;
        this.meshes.set("pole", pole);
        this.graphics.scene.add(pole);
    }
    addDoublemPendulum(length) {
        this.length = length;
        const bobGeometry = new THREE.SphereGeometry(0.2, 32, 32);
        const bobMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
        this.bob = new THREE.Mesh(bobGeometry, bobMaterial);
        this.graphics.scene.add(this.bob);
        const rodGeometry = new THREE.CylinderGeometry(0.1, 0.1, length, 32);
        const rodMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
        this.rod = new THREE.Mesh(rodGeometry, rodMaterial);
        this.rod.position.y = -length / 2;
        this.graphics.scene.add(this.rod);
        const rod2Geometry = new THREE.CylinderGeometry(0.1, 0.1, length, 32);
        const rod2Material = new THREE.MeshBasicMaterial({ color: 0xffffff });
        this.rod2 = new THREE.Mesh(rod2Geometry, rod2Material);
        this.rod2.position.y = -length / 2 - length;
        this.graphics.scene.add(this.rod2);
    }
    addPendulum(length) {
        const pivotGeometry = new THREE.SphereGeometry(0.2, 32, 32);
        const pivotMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
        const pivot = new THREE.Mesh(pivotGeometry, pivotMaterial);
        this.meshes.set("pivot", pivot);
        this.graphics.scene.add(pivot);
        const rodGeometry = new THREE.CylinderGeometry(0.1, 0.1, length, 32);
        const rodMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
        const rod = new THREE.Mesh(rodGeometry, rodMaterial);
        this.meshes.set("rod", rod);
        this.graphics.scene.add(rod);
    }
    addPlane(normalArray, distance, size) {
        const normal = new THREE.Vector3().fromArray(normalArray);
        const position = normal.clone().multiplyScalar(distance);
        const planeGeometry = new THREE.PlaneGeometry(size, size);
        const planeMaterial = new THREE.MeshPhongMaterial({
            color: 0x808080,
            side: THREE.DoubleSide,
            flatShading: true,
        });
        const plane = new THREE.Mesh(planeGeometry, planeMaterial);
        plane.position.copy(position);
        plane.lookAt(position.clone().add(normal));
        this.graphics.scene.add(plane);
    }
    addRodPendulum(length) {
        this.length = length;
        const bobGeometry = new THREE.SphereGeometry(0.2, 32, 32);
        const bobMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
        this.bob = new THREE.Mesh(bobGeometry, bobMaterial);
        this.graphics.scene.add(this.bob);
        const rodGeometry = new THREE.CylinderGeometry(0.1, 0.1, length, 32);
        const rodMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
        this.rod = new THREE.Mesh(rodGeometry, rodMaterial);
        this.rod.position.y = -length / 2;
        this.graphics.scene.add(this.rod);
    }
    addRimlessWheel(radius, n_foot) {
        // Add pivot at the center
        const pivotGeometry = new THREE.SphereGeometry(radius, 32, 32);
        const pivotMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
        const pivot = new THREE.Mesh(pivotGeometry, pivotMaterial);
        this.meshes.set("pivot", pivot);
        this.graphics.scene.add(pivot);
        // Add feet at the peripheral
        let radius_foot = 1.0;
        for (let i = 0; i < n_foot; i++) {
            const footGeometry = new THREE.SphereGeometry(radius_foot, 32, 32);
            const footMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
            const foot = new THREE.Mesh(footGeometry, footMaterial);
            this.meshes.set(`foot ${i + 1}`, foot);
            this.graphics.scene.add(foot);
        }
    }
    addCompassGait() {
        this.addSphere("hip", 0xff0000, 0.3);
        this.addCuboid("left leg", 0x00ff00, 0.1, 0.1, 2.0);
        this.addCuboid("right leg", 0x00ff00, 0.1, 0.1, 2.0);
    }
    addAcuatedAnkleHopper() {
        this.addSphere("foot", 0xffffff, 0.1);
        this.addSphere("ankle", 0x000000, 0.05);
        this.addSphere("body", 0x00ff00, 0.1);
    }
    addAcuatedHipHopper() {
        this.addCuboid("foot", 0xffffff, 0.2, 0.2, 0.2);
        this.addSphere("hip", 0x000000, 0.05);
        this.addCuboid("body", 0x00ff00, 0.2, 0.2, 0.2);
    }
    addQuadruped() {
        let l_hip = 0.2;
        let l_knee = 0.2;
        this.addCuboid("body", 0xffffff, 1.5, 0.5, 0.5);
        this.addCuboid("fr_hip", 0xff0000, l_hip, l_hip, l_hip);
        this.addCuboid("fr_knee", 0x00ff00, l_knee, l_knee, l_knee);
        this.addCuboid("fl_hip", 0xff0000, l_hip, l_hip, l_hip);
        this.addCuboid("fl_knee", 0x00ff00, l_knee, l_knee, l_knee);
        this.addCuboid("br_hip", 0xff0000, l_hip, l_hip, l_hip);
        this.addCuboid("br_knee", 0x00ff00, l_knee, l_knee, l_knee);
        this.addCuboid("bl_hip", 0xff0000, l_hip, l_hip, l_hip);
        this.addCuboid("bl_knee", 0x00ff00, l_knee, l_knee, l_knee);
        let radius_foot = 0.1;
        for (let i = 0; i < 12; i++) {
            const footGeometry = new THREE.SphereGeometry(radius_foot, 32, 32);
            const footMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff });
            const foot = new THREE.Mesh(footGeometry, footMaterial);
            this.meshes.set(`foot ${i + 1}`, foot);
            this.graphics.scene.add(foot);
        }
    }
    addPusher() {
        this.addCuboid("extension", 0xffffff, 0.5, 0.5, 0.5);
        this.addCuboid("lift", 0xff0000, 0.2, 0.2, 1.75);
        this.addCuboid("cube", 0x00ff00, 0.5, 0.5, 0.5);
    }
    addGripper() {
        this.addCuboid("lift", 0xff0000, 0.2, 0.2, 1.0);
        this.addCuboid("gripper_left", 0x00ff00, 0.1, 0.4, 0.4);
        this.addCuboid("gripper_right", 0x00ff00, 0.1, 0.4, 0.4);
        this.addCuboid("cube", 0x0000ff, 0.5, 0.5, 0.5);
    }
    add1DHopper(w_body, h_body, r_leg, r_foot) {
        const bodyGeometry = new THREE.BoxGeometry(w_body, w_body, h_body);
        const bodyMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
        const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
        this.meshes.set("hopper_body", body);
        this.graphics.scene.add(body);
        const legGeometry = new THREE.SphereGeometry(r_leg, 32, 32);
        const legMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
        const leg = new THREE.Mesh(legGeometry, legMaterial);
        this.meshes.set("hopper_leg", leg);
        this.graphics.scene.add(leg);
        const footGeometry = new THREE.SphereGeometry(r_foot, 32, 32);
        const footMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff });
        const foot = new THREE.Mesh(footGeometry, footMaterial);
        this.meshes.set("hopper_foot", foot);
        this.graphics.scene.add(foot);
    }
    add2DHopper(w_body, h_body, r_hip, r_piston, l_leg) {
        this.addCuboid("hopper_body", 0x00ff00, w_body, w_body, h_body);
        this.addSphere("hopper_hip", 0xff0000, r_hip);
        this.addLine("hopper_body_hip_line", 0xff0000, 3);
        this.addSphere("hopper_piston", 0xffffff, r_piston);
        this.addCuboid("hopper_leg", 0x0000ff, 0.1, 0.1, l_leg);
    }
    addFourBarLinkage() {
        let w = 0.1;
        let l = 1.0;
        let offset = new Matrix4().makeTranslation(0, 0, -l / 2.0);
        this.addCuboid("bar1", 0xff0000, w, w, l, offset);
        this.addCuboid("bar2", 0xff0000, w, w, l, offset);
        let linkageOffset = new Matrix4().makeTranslation(l / 2.0, 0, 0);
        this.addCuboid("bar3", 0x00ff00, l, w, w, linkageOffset);
    }
    addFourBarLinkageWithBase() {
        let w = 0.1;
        let l = 1.0;
        this.addCuboid("base", 0x0000ff, l, w, w);
        let offset = new Matrix4().makeTranslation(0, 0, -l / 2.0);
        this.addCuboid("bar1", 0xff0000, w, w, l, offset);
        this.addCuboid("bar2", 0xff0000, w, w, l, offset);
        let linkageOffset = new Matrix4().makeTranslation(l / 2.0, 0, 0);
        this.addCuboid("bar3", 0x00ff00, l, w, w, linkageOffset);
    }
    addMockNavbot() {
        let w = 0.1;
        let l = 1.0;
        this.addCuboid("base", 0x0000ff, l, w, w);
        let offset = new Matrix4().makeTranslation(0, 0, -l / 2.0);
        this.addCuboid("right_leg", 0xff0000, w, w, l, offset);
        this.addCuboid("right_link", 0xff0000, w, w, l, offset);
        this.addCuboid("left_leg", 0xff0000, w, w, l, offset);
        this.addCuboid("left_link", 0xff0000, w, w, l, offset);
        let linkageOffset = new Matrix4().makeTranslation(0, l / 2.0, 0);
        this.addCuboid("right_foot", 0x00ff00, w, l, w, linkageOffset);
        this.addCuboid("left_foot", 0x00ff00, w, l, w, linkageOffset);
    }
    addNavbot() {
        // Panel-cutted Carbon Fiber
        let carbonFiberMaterial = new THREE.MeshStandardMaterial({
            color: 0x262626,
            roughness: 0.3,
            metalness: 0.4,
            flatShading: true,
        });
        this.addMeshSetWithMaterial(0, "base", carbonFiberMaterial);
        let frontPlateMaterial = new THREE.MeshStandardMaterial({
            color: 0xbebebe,
            roughness: 0.3,
            metalness: 1.0,
            flatShading: true,
        });
        this.meshSet.get("base")[1].material = frontPlateMaterial;
        let aluminumMaterial = new THREE.MeshStandardMaterial({
            color: 0xd9d9d9,
            metalness: 1.0,
            roughness: 0.3, // somewhat shiny but not mirror-like
        });
        this.meshSet.get("base")[3].material = aluminumMaterial;
        this.meshSet.get("base")[4].material = aluminumMaterial;
        // SLS 3D printed
        let nylonSLSMaterial = new THREE.MeshStandardMaterial({
            color: 0x2a2a2a,
            roughness: 0.9,
            metalness: 0.0,
            flatShading: true,
        });
        this.meshSet.get("base")[7].material = nylonSLSMaterial;
        this.meshSet.get("base")[8].material = nylonSLSMaterial;
        this.meshSet.get("base")[9].material = aluminumMaterial;
        this.meshSet.get("base")[10].material = aluminumMaterial;
        this.addMeshSetWithMaterial(1, "leg_left", nylonSLSMaterial);
        this.addMeshSetWithMaterial(2, "foot_left", nylonSLSMaterial);
        // stainless steel
        let stainlessSteelMaterial = new THREE.MeshStandardMaterial({
            color: 0xb0b0b0,
            metalness: 1.0,
            roughness: 0.2,
            flatShading: true,
        });
        this.addMeshSetWithMaterial(3, "link_left", carbonFiberMaterial);
        this.meshSet.get("link_left")[1].material = stainlessSteelMaterial;
        this.meshSet.get("link_left")[2].material = stainlessSteelMaterial;
        // Rubber black
        let rubberMaterial = new THREE.MeshStandardMaterial({
            color: 0x080808,
            roughness: 0.9,
            metalness: 0.0,
            flatShading: true,
        });
        this.addMeshSetWithMaterial(4, "wheel_left", nylonSLSMaterial);
        this.meshSet.get("wheel_left")[1].material = rubberMaterial;
        this.addMeshSetWithMaterial(5, "leg_right", nylonSLSMaterial);
        this.addMeshSetWithMaterial(6, "foot_right", nylonSLSMaterial);
        this.addMeshSetWithMaterial(7, "link_right", carbonFiberMaterial);
        this.meshSet.get("link_right")[1].material = stainlessSteelMaterial;
        this.meshSet.get("link_right")[2].material = stainlessSteelMaterial;
        this.addMeshSetWithMaterial(8, "wheel_right", nylonSLSMaterial);
        this.meshSet.get("wheel_right")[1].material = rubberMaterial;
    }
    updateNavbot() {
        this.updateMeshSet(0, "base");
        this.updateMeshSet(1, "leg_left");
        this.updateMeshSet(2, "foot_left");
        this.updateMeshSet(3, "link_left");
        this.updateMeshSet(4, "wheel_left");
        this.updateMeshSet(5, "leg_right");
        this.updateMeshSet(6, "foot_right");
        this.updateMeshSet(7, "link_right");
        this.updateMeshSet(8, "wheel_right");
    }
    updateRodPose(angle) {
        // Update rod center-of-shape position
        this.rod.position.x = (this.length / 2) * Math.sin(angle);
        this.rod.position.y = (-this.length / 2) * Math.cos(angle);
        // Rotate rod
        this.rod.rotation.z = angle;
    }
    updateDoublemPendulumPose(angle1, angle2) {
        this.updateRodPose(angle1);
        this.rod2.position.x =
            this.length * Math.sin(angle1) +
                (this.length / 2) * Math.sin(angle1 + angle2);
        this.rod2.position.y =
            -this.length * Math.cos(angle1) -
                (this.length / 2) * Math.cos(angle1 + angle2);
        this.rod2.rotation.z = angle1 + angle2;
    }
    updateCartPose(pos) {
        this.cart.position.x = pos;
    }
    updateCartPole(cart_pos, pole_angle) {
        let cart = this.meshes.get("cart");
        cart.position.x = cart_pos;
        let pole = this.meshes.get("pole");
        const length = pole.geometry.parameters.height;
        pole.position.x = cart_pos + (length / 2) * Math.sin(pole_angle);
        pole.position.y = -(length / 2) * Math.cos(pole_angle);
        pole.rotation.z = pole_angle;
    }
    updatePendulumPose(angle) {
        let rod = this.meshes.get("rod");
        const length = rod.geometry.parameters.height;
        rod.position.x = (length / 2) * Math.sin(angle);
        rod.position.y = (-length / 2) * Math.cos(angle);
        rod.rotation.z = angle;
    }
    updateSpherePose(poses) {
        let euler = [poses[0], poses[1], poses[2]];
        let pos = [poses[3], poses[4], poses[5]];
        let body = this.meshes.get("sphere");
        body.rotation.set(euler[0], euler[1], euler[2]);
        body.position.set(pos[0], pos[1], pos[2]);
    }
    updateRimlessWheel(poses, contact_positions) {
        let euler = [poses[0], poses[1], poses[2]];
        let pos = [poses[3], poses[4], poses[5]];
        let body = this.meshes.get("pivot");
        body.rotation.set(euler[0], euler[1], euler[2]);
        body.position.set(pos[0], pos[1], pos[2]);
        for (let i = 0; i < contact_positions.length; i += 3) {
            let foot = this.meshes.get(`foot ${i / 3 + 1}`);
            foot.position.set(contact_positions[i], contact_positions[i + 1], contact_positions[i + 2]);
        }
    }
    updateCompassGait(poses) {
        let hipPose = poses.subarray(0, 6);
        this.setPose("hip", hipPose);
        let leftLegPose = poses.subarray(6, 12);
        this.setPose("left leg", leftLegPose);
        let rightLegPose = poses.subarray(12, 18);
        this.setPose("right leg", rightLegPose);
    }
    updateActuatedAnkleHopper(poses) {
        let footPose = poses.subarray(0, 6);
        this.setPose("foot", footPose);
        let anklePose = poses.subarray(6, 12);
        this.setPose("ankle", anklePose);
        let bodyPose = poses.subarray(12, 18);
        this.setPose("body", bodyPose);
    }
    updateActuatedHipHopper(poses) {
        let footPose = poses.subarray(0, 6);
        this.setPose("foot", footPose);
        let hipPose = poses.subarray(6, 12);
        this.setPose("hip", hipPose);
        let bodyPose = poses.subarray(12, 18);
        this.setPose("body", bodyPose);
    }
    updateQuadruped(poses, contact_positions) {
        let i = 0;
        let bodyPose = poses.subarray(i, i + 6);
        this.setPose("body", bodyPose);
        i += 6;
        let frHipPose = poses.subarray(i, i + 6);
        this.setPose("fr_hip", frHipPose);
        i += 6;
        let frKneePose = poses.subarray(i, i + 6);
        this.setPose("fr_knee", frKneePose);
        i += 6;
        let flHipPose = poses.subarray(i, i + 6);
        this.setPose("fl_hip", flHipPose);
        i += 6;
        let flKneePose = poses.subarray(i, i + 6);
        this.setPose("fl_knee", flKneePose);
        i += 6;
        let brHipPose = poses.subarray(i, i + 6);
        this.setPose("br_hip", brHipPose);
        i += 6;
        let brKneePose = poses.subarray(i, i + 6);
        this.setPose("br_knee", brKneePose);
        i += 6;
        let blHipPose = poses.subarray(i, i + 6);
        this.setPose("bl_hip", blHipPose);
        i += 6;
        let blKneePose = poses.subarray(i, i + 6);
        this.setPose("bl_knee", blKneePose);
        for (let i = 0; i < contact_positions.length; i += 3) {
            let foot = this.meshes.get(`foot ${i / 3 + 1}`);
            foot.position.set(contact_positions[i], contact_positions[i + 1], contact_positions[i + 2]);
        }
    }
    updatePusher(poses) {
        let i = 0;
        let extensionPose = poses.subarray(i, i + 6);
        this.setPose("extension", extensionPose);
        i += 6;
        let liftPose = poses.subarray(i, i + 6);
        this.setPose("lift", liftPose);
        i += 6;
        let cubePose = poses.subarray(i, i + 6);
        this.setPose("cube", cubePose);
    }
    // TODO: automate this
    updateGripper(poses) {
        let i = 0;
        let liftPose = poses.subarray(i, i + 6);
        this.setPose("lift", liftPose);
        i += 6;
        let gripperLeftPose = poses.subarray(i, i + 6);
        this.setPose("gripper_left", gripperLeftPose);
        i += 6;
        let gripperRightPose = poses.subarray(i, i + 6);
        this.setPose("gripper_right", gripperRightPose);
        i += 6;
        let cubePose = poses.subarray(i, i + 6);
        this.setPose("cube", cubePose);
    }
    update1DHopper(poses) {
        let body_euler = [poses[0], poses[1], poses[2]];
        let body_pos = [poses[3], poses[4], poses[5]];
        let body = this.meshes.get("hopper_body");
        body.rotation.set(body_euler[0], body_euler[1], body_euler[2]);
        body.position.set(body_pos[0], body_pos[1], body_pos[2]);
        let leg_euler = [poses[6], poses[7], poses[8]];
        let leg_pos = [poses[9], poses[10], poses[11]];
        let leg = this.meshes.get("hopper_leg");
        leg.rotation.set(leg_euler[0], leg_euler[1], leg_euler[2]);
        leg.position.set(leg_pos[0], leg_pos[1], leg_pos[2]);
        let foot_euler = [poses[12], poses[13], poses[14]];
        let foot_pos = [poses[15], poses[16], poses[17]];
        let foot = this.meshes.get("hopper_foot");
        foot.rotation.set(foot_euler[0], foot_euler[1], foot_euler[2]);
        foot.position.set(foot_pos[0], foot_pos[1], foot_pos[2]);
    }
    update2DHopper(poses) {
        let bodyPose = poses.subarray(0, 6);
        this.setPose("hopper_body", bodyPose);
        let hipPose = poses.subarray(6, 12);
        this.setPose("hopper_hip", hipPose);
        this.setLineEndpoints("hopper_body_hip_line", bodyPose.subarray(3, 6), hipPose.subarray(3, 6));
        this.setPose("hopper_piston", poses.subarray(12, 18));
        let legGeometry = this.meshes.get("hopper_leg")
            .geometry;
        let legLength = legGeometry.parameters.depth;
        let legPose = poses.subarray(18, 24);
        let legEuler = new THREE.Euler(legPose[0], legPose[1], legPose[2]);
        let legQuaternion = new THREE.Quaternion();
        legQuaternion.setFromEuler(legEuler);
        let legPositionOffset = new THREE.Vector3(0, 0, legLength / 2.0);
        legPositionOffset.applyQuaternion(legQuaternion);
        legPose[3] += legPositionOffset.x;
        legPose[4] += legPositionOffset.y;
        legPose[5] += legPositionOffset.z;
        this.setPose("hopper_leg", legPose);
    }
    updateBalancingBotPose(poses) {
        let bodyPose = poses.subarray(0, 6);
        this.setPose("body", bodyPose);
        let wheelLeftPose = poses.subarray(6, 12);
        this.setPose("wheel_left", wheelLeftPose);
        let wheelRightPose = poses.subarray(12, 18);
        this.setPose("wheel_right", wheelRightPose);
    }
    updateFourBarLinkage(poses) {
        let bar1Pose = poses.subarray(0, 6);
        this.setPose("bar1", bar1Pose);
        let bar2Pose = poses.subarray(6, 12);
        this.setPose("bar2", bar2Pose);
        let bar3Pose = poses.subarray(12, 18);
        this.setPose("bar3", bar3Pose);
    }
    updateFourBarLinkageWithBase(poses) {
        let i = 0;
        let basePose = poses.subarray(i, i + 6);
        this.setPose("base", basePose);
        i += 6;
        let bar1Pose = poses.subarray(i, i + 6);
        this.setPose("bar1", bar1Pose);
        i += 6;
        let bar2Pose = poses.subarray(i, i + 6);
        this.setPose("bar2", bar2Pose);
        i += 6;
        let bar3Pose = poses.subarray(i, i + 6);
        this.setPose("bar3", bar3Pose);
    }
    updateMockNavbot(poses) {
        let i = 0;
        let basePose = poses.subarray(i, i + 6);
        this.setPose("base", basePose);
        i += 6;
        let right_leg_pose = poses.subarray(i, i + 6);
        this.setPose("right_leg", right_leg_pose);
        i += 6;
        let right_link_pose = poses.subarray(i, i + 6);
        this.setPose("right_link", right_link_pose);
        i += 6;
        let left_leg_pose = poses.subarray(i, i + 6);
        this.setPose("left_leg", left_leg_pose);
        i += 6;
        let left_link_pose = poses.subarray(i, i + 6);
        this.setPose("left_link", left_link_pose);
        i += 6;
        let right_foot_pose = poses.subarray(i, i + 6);
        this.setPose("right_foot", right_foot_pose);
        i += 6;
        let left_foot_pose = poses.subarray(i, i + 6);
        this.setPose("left_foot", left_foot_pose);
    }
    /// Note: timestamp is in milliseconds
    async run(n_substeps, timestamp, keysPressed) {
        const renderStart = performance.now();
        this.graphics.render();
        this.renderMs += 0.1 * (performance.now() - renderStart - this.renderMs);
        // The physics step is fixed -- contacts are velocity-level and tolerances
        // are tuned against it, so it is the number of substeps a frame takes that
        // varies with the wall clock below, never their length.
        const dt = 1 / this.fps;
        const subDt = dt / n_substeps;
        let control_input = new FloatArray(3);
        let joyx = 1.0; // 100.0;
        if (keysPressed != null) {
            if (keysPressed["a"]) {
                control_input[0] = -joyx;
            }
            else if (keysPressed["d"]) {
                control_input[0] = joyx;
            }
            else {
                control_input[0] = 0.0;
            }
            let joyy = 1.0; // 100.0
            if (keysPressed["w"]) {
                control_input[1] = joyy;
            }
            else if (keysPressed["s"]) {
                control_input[1] = -joyy;
            }
            else {
                control_input[1] = 0.0;
            }
            if (keysPressed[" "]) {
                control_input[2] = 1.0; // jump
            }
            else {
                control_input[2] = 0.0; // stop
            }
        }
        // // drop the arm
        // if (keysPressed["s"]) {
        //   control_input[0] = 1.0;
        // } else {
        //   control_input[0] = 0.0;
        // }
        // // close the gripper
        // if (keysPressed[" "]) {
        //   control_input[1] = 1.0;
        // } else {
        //   control_input[1] = 0.0;
        // }
        let slow_motion_factor = 1; // create slow motion effect to better see the details of motion
        const wallElapsed = this.lastWallTime === 0 ? 0 : (timestamp - this.lastWallTime) / 1000; // seconds
        this.lastWallTime = timestamp;
        // Before the accumulators rather than after: paused time is time the
        // simulation never owed, so it must not pile up on `stepAccumulator` to be
        // caught up on resume, and it must not count against `realtimeRatio`.
        // `lastWallTime` is up to date above, so the first frame back sees one
        // frame's worth of elapsed time and no more.
        if (this.paused) {
            requestAnimationFrame((timestamp) => this.run(n_substeps, timestamp, keysPressed));
            return;
        }
        this.wallTimeAccumulator += wallElapsed;
        if (this.wallTimeAccumulator >= this.rtrSmoothingWindow) {
            this.realtimeRatio = this.simTimeAccumulator / this.wallTimeAccumulator;
            // Reset accumulators for next window
            this.simTimeAccumulator = 0;
            this.wallTimeAccumulator = 0;
        }
        // Size this frame's physics budget off how the last frame went. A frame
        // can only be shown on a vsync, so one that overruns the refresh interval
        // by a little waits a whole extra interval: stepping a fixed amount per
        // frame left a scene a tenth too slow at half its frame rate and half of
        // real time. Stepping only what fits keeps the frames on time, and a scene
        // that cannot keep up runs short of real time by what it lacks.
        //
        // What fits is not worked out from the scene -- nothing here knows what a
        // step costs, and it changes as contacts come and go or a robot is dropped
        // in -- but found: the budget creeps up while frames arrive on time and is
        // cut back by a fifth whenever one is late, which settles it just under
        // the most that fits, and lets it settle again when the scene changes.
        // Whatever else the page does each frame is in that answer too.
        if (wallElapsed > 0) {
            const interval = wallElapsed * 1000;
            // Drifts up a little each frame, so a window moved to a slower display
            // is noticed, and snaps down to any shorter interval it sees.
            this.refreshMs = Math.min(this.refreshMs * 1.01, interval);
            // The frame a scene that needs the time aims at: one physics frame, or
            // one vsync if the display is slower than that. Not one vsync on a
            // faster display -- drawing 120 times a second costs twice the drawing
            // and leaves the physics a sliver of each frame, which ran this robot at
            // two thirds of real time where 60 frames a second keep up. A light scene
            // is done long before the budget runs out and still gets every vsync.
            const period = Math.max(this.refreshMs, 1000 / this.fps);
            const late = interval > period + this.refreshMs / 2;
            // Below this the frame is drawing and little else; above the ceiling it
            // would miss its vsync even with nothing else to do.
            const floor = 1;
            const ceiling = Math.max(floor, period - this.renderMs - 2);
            this.physicsBudgetMs = late
                ? Math.max(floor, this.physicsBudgetMs * 0.8)
                : Math.min(ceiling, this.physicsBudgetMs + 0.25);
        }
        // How much sim time this frame owes: the wall time since the last one,
        // plus whatever earlier frames left unstepped -- up to `maxCatchUpSteps`
        // frames of that, past which it is time the simulation cannot buy back,
        // dropped rather than queued. Stepping a fixed amount per frame instead
        // would tie the speed of the simulation to the refresh rate.
        this.stepAccumulator =
            Math.min(this.stepAccumulator, this.maxCatchUpSteps * dt) +
                wallElapsed / slow_motion_factor;
        // Whole substeps until the debt is paid or the budget is spent, and always
        // at least one, so a scene whose every substep outlasts a frame still
        // moves. The leftover stays on the accumulator for the next frame.
        let stepped = 0;
        if (this.hybrid) {
            const deadline = performance.now() + this.physicsBudgetMs;
            while (this.stepAccumulator >= subDt) {
                this.hybrid.step(subDt, control_input);
                this.stepAccumulator -= subDt;
                stepped++;
                if (performance.now() >= deadline) {
                    break;
                }
            }
        }
        else {
            // Nothing to step, so the time passes all the same.
            stepped = Math.floor(this.stepAccumulator / subDt);
            this.stepAccumulator -= stepped * subDt;
        }
        if (stepped > 0) {
            this.simTimeAccumulator += stepped * subDt;
            if (this.hybrid) {
                this.updateHybrid();
            }
            this.lastStepTime = timestamp;
        }
        requestAnimationFrame((timestamp) => this.run(n_substeps, timestamp, keysPressed));
    }
}
